// Minimal i18n runtime for the scheduler app.
//
// Deliberately dependency-free: this is an ejected CRA pinned to Node 14 / webpack 4, and the
// feature only needs key lookup plus {param} interpolation. The API surface mirrors the sibling
// module `administracja-panelu-rezydenta-front` (vue-i18n) so the two apps stay consistent.
//
// Usage:
//   function components -> useTranslation()      -> { t, locale, setLocale }
//   class components    -> withTranslation(Cmp)  -> same values as props
//   plain modules       -> import { t }          -> call t() at CALL TIME, never at module scope

import { locale as setPrimeLocale, addLocale } from "primereact/api";
import { pl } from "./locales/pl";
import { en } from "./locales/en";
import { primeLocales } from "./prime";

export const LOCALE_STORAGE_KEY = "hz.locale";
export const SUPPORTED_LOCALES = ["pl", "en"];
export const DEFAULT_LOCALE = "pl";

const messages = { pl, en };
const listeners = [];

let activeLocale = DEFAULT_LOCALE;

const isSupported = (candidate) => SUPPORTED_LOCALES.indexOf(candidate) > -1;

// Read synchronously at module load so the locale is applied before ReactDOM.render and the
// user never sees a flash of Polish. No browser-language detection: the default is always PL.
export function getInitialLocale() {
   try {
      const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
      return isSupported(saved) ? saved : DEFAULT_LOCALE;
   } catch (error) {
      // localStorage can throw in private-browsing modes; fall back to the default.
      return DEFAULT_LOCALE;
   }
}

export function getLocale() {
   return activeLocale;
}

const resolve = (tree, key) =>
   key.split(".").reduce((node, part) => (node == null ? undefined : node[part]), tree);

const interpolate = (template, params) => {
   if (!params) return template;
   return template.replace(/\{(\w+)\}/g, (match, name) =>
      Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match
   );
};

export function t(key, params) {
   let message = resolve(messages[activeLocale], key);
   if (typeof message !== "string") message = resolve(messages[DEFAULT_LOCALE], key);
   if (typeof message !== "string") {
      if (process.env.NODE_ENV !== "production") {
         console.warn(`[i18n] missing translation key: ${key}`);
      }
      return key;
   }
   return interpolate(message, params);
}

export function subscribe(listener) {
   listeners.push(listener);
   return () => {
      const index = listeners.indexOf(listener);
      if (index > -1) listeners.splice(index, 1);
   };
}

// Switches the language WITHOUT persisting it. Used by the cross-tab storage listener so two
// tabs cannot echo writes back at each other.
export function applyLocale(nextLocale) {
   activeLocale = isSupported(nextLocale) ? nextLocale : DEFAULT_LOCALE;
   setPrimeLocale(activeLocale);
   document.documentElement.lang = activeLocale;
   document.title = t("common.appTitle");
   listeners.slice().forEach((listener) => listener(activeLocale));
}

export function setLocale(nextLocale) {
   applyLocale(nextLocale);
   try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, activeLocale);
   } catch (error) {
      // Persistence is best-effort; the language still applies for this session.
   }
}

// PrimeReact replaces the whole locale object on a swap (no deep merge with defaults), so both
// bundles must be complete. Registered once, here, at module load.
SUPPORTED_LOCALES.forEach((supported) => addLocale(supported, primeLocales[supported]));
